import { Suspense, lazy, ComponentType } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface LazyComponentProps {
  fallbackHeight?: string;
}

// Loading fallback component
const SectionSkeleton = ({ height = "400px" }: { height?: string }) => (
  <div className="w-full py-20" style={{ minHeight: height }}>
    <div className="container mx-auto">
      <div className="flex flex-col items-center gap-4">
        <Skeleton className="h-8 w-32 rounded-full" />
        <Skeleton className="h-10 w-96 max-w-full" />
        <Skeleton className="h-6 w-80 max-w-full" />
        <div className="grid md:grid-cols-3 gap-6 w-full mt-8">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    </div>
  </div>
);

// Lazy load wrapper with suspense
export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  const Component = lazy(factory);
  
  // Add preload method
  (Component as any).preload = factory;
  
  return Component;
}

// Create a lazy section wrapper
export function LazySection({
  component: Component,
  fallbackHeight = "400px",
  ...props
}: {
  component: React.LazyExoticComponent<ComponentType<any>>;
  fallbackHeight?: string;
  [key: string]: any;
}) {
  return (
    <Suspense fallback={<SectionSkeleton height={fallbackHeight} />}>
      <Component {...props} />
    </Suspense>
  );
}

export { SectionSkeleton };
